import type * as THREE from 'three/webgpu'
import { ImportedModelObjectMetadataService } from '../imported-model/metadata'

/**
 * 解析线性零部件阵列中的组件级显示名称。
 *
 * 设计说明：
 * 1. 阵列计算必须保存可变换根 UUID，但该根可能是名称为 `imported-brep:*` 的 Body Mesh；
 * 2. 用户可见名称属于组件 occurrence，应优先来自 selection proxy 或 hierarchy selection 根；
 * 3. PropertyManager、提交记录和阵列复制体必须复用同一解析结果，禁止各自回退对象名称。
 */
export class LinearComponentPatternComponentDisplayNameResolver {
  /**
   * 创建组件显示名称解析器。
   *
   * @param scene 当前 Viewer 场景，用于按装配节点身份查找组件语义根。
   * @param findSceneObjectById Viewer 的稳定对象查询门面。
   */
  public constructor(
    private readonly scene: THREE.Scene,
    private readonly findSceneObjectById: (objectId: string) => THREE.Object3D | null,
  ) {}

  /**
   * 按场景对象 ID 解析组件显示名称。
   *
   * @param objectId 阵列控制器保存的组件变换根 UUID。
   * @returns 用户可见名称；对象失效时回退原 ID。
   */
  public resolveByObjectId(objectId: string): string {
    const normalizedObjectId = objectId.trim()
    if (!normalizedObjectId) {
      return objectId
    }
    const object = this.findSceneObjectById(normalizedObjectId)
    return object ? this.resolve(object) : normalizedObjectId
  }

  /**
   * 解析一个阵列组件对象的用户可见名称。
   *
   * @param object 组件变换根、可见 Mesh 或选择代理。
   * @returns 组件 occurrence 名称。
   */
  public resolve(object: THREE.Object3D): string {
    const semanticCandidates = this.collectSemanticCandidates(object)
    const semanticHierarchyName = semanticCandidates
      .map(candidate => this.resolveComponentHierarchyName(candidate))
      .find(name => name.length > 0) ?? ''
    if (semanticHierarchyName) {
      return semanticHierarchyName
    }

    const semanticObjectName = semanticCandidates
      .map(candidate => candidate.name.trim())
      .find(name => name.length > 0) ?? ''
    if (semanticObjectName) {
      return semanticObjectName
    }

    const ownHierarchyName = ImportedModelObjectMetadataService
      .resolveHierarchy(object)
      ?.name
      ?.trim() ?? ''
    if (ownHierarchyName) {
      return ownHierarchyName
    }

    const objectName = object.name.trim()
    return objectName || object.type || object.uuid
  }

  /**
   * 收集当前对象对应的组件语义候选，并保持 occurrence 级身份优先级。
   *
   * @param object 当前阵列选择对象。
   * @returns 去重后的组件候选。
   */
  private collectSemanticCandidates(object: THREE.Object3D): THREE.Object3D[] {
    const candidatesById = new Map<string, THREE.Object3D>()
    let current: THREE.Object3D | null = object
    while (current) {
      this.collectMetadataCandidateIds(current).forEach((candidateId) => {
        const candidate = this.resolveIdentityObject(candidateId)
        if (candidate) {
          candidatesById.set(candidate.uuid, candidate)
        }
      })
      if (this.resolveComponentHierarchyName(current)) {
        candidatesById.set(current.uuid, current)
      }
      current = current.parent
    }
    return [...candidatesById.values()]
  }

  /**
   * 收集对象 metadata 声明的组件身份候选。
   *
   * @param object 当前对象。
   * @returns 非空身份 ID 列表。
   */
  private collectMetadataCandidateIds(object: THREE.Object3D): string[] {
    const metadata = ImportedModelObjectMetadataService.peekMetadata(object)
    return [
      metadata?.selectionProxyObjectId,
      metadata?.hierarchy?.meta?.selectionObjectId,
      metadata?.selectionTarget?.assemblyNodeId,
      metadata?.assemblyNodeId,
    ]
      .map(value => value?.trim() ?? '')
      .filter(value => value.length > 0)
  }

  /**
   * 按 Object3D UUID 或 assemblyNodeId 解析组件语义对象。
   *
   * @param identityId 选择代理、层级选择对象或装配节点 ID。
   * @returns 命中的场景对象。
   */
  private resolveIdentityObject(identityId: string): THREE.Object3D | null {
    const directObject = this.findSceneObjectById(identityId)
    if (directObject) {
      return directObject
    }

    let matchedObject: THREE.Object3D | null = null
    this.scene.traverse((candidate) => {
      if (matchedObject || !this.resolveComponentHierarchyName(candidate)) {
        return
      }
      const metadata = ImportedModelObjectMetadataService.peekMetadata(candidate)
      if (
        metadata?.assemblyNodeId?.trim() === identityId
        || metadata?.selectionTarget?.assemblyNodeId?.trim() === identityId
      ) {
        matchedObject = candidate
      }
    })
    return matchedObject
  }

  /**
   * 读取 part/assembly 语义对象的 hierarchy 名称。
   *
   * Body 名称通常是内部 BRep 身份，不能抢占组件 occurrence 名称。
   *
   * @param object 候选组件对象。
   * @returns 有效组件名称；非组件语义返回空字符串。
   */
  private resolveComponentHierarchyName(object: THREE.Object3D): string {
    const hierarchy = ImportedModelObjectMetadataService.resolveHierarchy(object)
    if (
      hierarchy?.meta?.semanticKind !== 'part'
      && hierarchy?.meta?.semanticKind !== 'assembly'
    ) {
      return ''
    }
    return hierarchy.name?.trim() ?? ''
  }
}
